/* eslint-disable consistent-default-export-name/default-export-match-filename */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useDDPSubscription } from '@zedoc/ddp-connector';
import DashboardTable from './DashboardTable';
import {
  PROJECT_DASHBOARD_TYPE__EAPP_DASHBOARD_V2,
  DASHBOARD_PERSPECTIVE__EAPPS,
  DASHBOARD_PERSPECTIVE__PATIENTS,
  DASHBOARD_PERSPECTIVE__RESPONSES,
  DASHBOARD_PERSPECTIVE__ACTIVITIES,
  DASHBOARD_PERSPECTIVE__PARTICIPATIONS,
} from '../../../../common/constants';
import { default as RecipientSelect } from '../../../../common/selectors/Recipient';
import createSelectors from '../../../../common/selectors/createSelectors';
import { selectAllProjectMilestones } from '../../selectors';
import EAPP from '../../../../common/models/EAPP';
import usePagination from '../../../../utils/usePagination';
import { apiZedocSearchEAPPs } from '../../../../common/api/zedoc';
import { registerType } from '../../dashboards';
import Stack from '../../../../common/components/primitives/Stack';
import ConnectedFilters from './ConnectedFilters';
import store from '../../store';
import cleanFilters from '../../../../utils/cleanFilters';
// import DashboardNotesModal from './DashboardNotesModal';

const EAPPSelect = createSelectors(EAPP);

const DashboardEAppV2 = ({
  projectId,
  answersSheetId,
  recipientId,
  participationId,
  activityId,
  dashboardId,
  hideColumns,
}) => {
  const rawFilters = useSelector(
    store.get(`dashboards.${dashboardId}.filters`),
  );
  const filters = useMemo(
    () => cleanFilters(rawFilters, { anyOf: true }),
    [rawFilters],
  );

  const {
    // ready: subscriptionsReady,
    items: dataSource,
    getPaginationProps,
  } = usePagination({
    selector: EAPPSelect,
    mapSelector: (selector) => {
      return selector.lookup({
        from: RecipientSelect.all(),
        as: 'recipient',
        foreignKey: '_id',
        key: (doc, _, byRecipientId) => {
          return (
            byRecipientId[doc.recipientId] && byRecipientId[doc.recipientId][0]
          );
        },
      });
    },
    getSubscription: (currentPage, resultsPerPage) => {
      const params = {
        controlId: '$meta.id',
        pageIndex: currentPage - 1,
        resultsPerPage,
        filters,
      };

      if (projectId) {
        params.projectId = projectId;
      }

      if (answersSheetId) {
        params.answersSheetId = answersSheetId;
      }

      if (recipientId) {
        params.recipientId = recipientId;
      }

      if (participationId) {
        params.participationId = participationId;
      }

      if (activityId) {
        params.activityId = activityId;
      }

      return apiZedocSearchEAPPs.withParams(params);
    },
  });

  useDDPSubscription({
    name: 'Users.projectMembers',
    params: [projectId],
  });

  const projectMilestones = useSelector(selectAllProjectMilestones);

  return (
    <Stack>
      <ConnectedFilters
        storeKey={`dashboards.${dashboardId}`}
        projectId={projectId}
        recipientId={recipientId}
        participationId={participationId}
        activityId={activityId}
        answersSheetId={answersSheetId}
        dashboardId={dashboardId}
        projectMilestones={projectMilestones}
        hideColumns={hideColumns}
      />
      <DashboardTable
        dataSource={dataSource}
        projectMilestones={projectMilestones}
        pagination={getPaginationProps()}
        hideColumns={hideColumns}
      />
    </Stack>
  );
};

DashboardEAppV2.propTypes = {
  projectId: PropTypes.string,
  answersSheetId: PropTypes.string,
  recipientId: PropTypes.string,
  participationId: PropTypes.string,
  activityId: PropTypes.string,
  dashboardId: PropTypes.string,
  hideColumns: PropTypes.arrayOf(PropTypes.string),
};

DashboardEAppV2.defaultProps = {
  projectId: null,
  answersSheetId: null,
  recipientId: null,
  participationId: null,
  activityId: null,
  dashboardId: null,
  hideColumns: null,
};

registerType(PROJECT_DASHBOARD_TYPE__EAPP_DASHBOARD_V2, {
  component: DashboardEAppV2,
  perspectives: [
    {
      genericView: true,
      perspective: DASHBOARD_PERSPECTIVE__EAPPS,
      specializations: [
        DASHBOARD_PERSPECTIVE__PATIENTS,
        DASHBOARD_PERSPECTIVE__PARTICIPATIONS,
        DASHBOARD_PERSPECTIVE__ACTIVITIES,
        DASHBOARD_PERSPECTIVE__RESPONSES,
      ],
    },
  ],
});

export default DashboardEAppV2;
